import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Sparkles, Loader2, Pencil, Eye, Share2, Plus, Crown } from "lucide-react";
import { toast } from "sonner";

const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "");

// Reusable Nearfluence Pro treatment for the Media Kit. variant: "dashboard" | "profile".
export function MediaKitProCard({ variant = "dashboard" }) {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/creator/media-kit/summary").then((r) => setS(r.data)).catch(() => setS(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!s) return null;

  const goEditor = () => navigate("/creator/media-kit");
  const preview = () => {
    if (s.published && s.share_id) window.open(`${PUBLIC_BASE}/m/${s.share_id}`, "_blank", "noopener");
    else goEditor();
  };
  const share = async () => {
    const url = `${PUBLIC_BASE}/m/${s.share_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: "My Nearfluence media kit", url }); return; }
      catch (err) { if (err && err.name === "AbortError") return; /* else fall through to copy */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Media kit link copied"); }
    catch { toast.message(url); }
  };

  const isProfile = variant === "profile";
  const Badge = (
    <span data-testid="nf-pro-badge" className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
      <Sparkles className="h-3 w-3" /> {isProfile ? "Pro" : "Nearfluence Pro"}
    </span>
  );

  // Determine state → primary CTA + secondary actions
  let cta = null;      // { label, onClick, testid }
  let secondary = [];  // [{ label, onClick, Icon, testid }]
  let statusChip = null;
  if (!s.is_pro) {
    cta = { label: "Explore Creator Pro", onClick: () => navigate("/plans"), testid: "mk-card-explore-pro" };
  } else if (!s.has_kit) {
    cta = { label: "Create Media Kit", onClick: goEditor, testid: "mk-card-create", Icon: Plus };
  } else if (s.draft) {
    cta = { label: "Continue Editing", onClick: goEditor, testid: "mk-card-continue", Icon: Pencil };
    statusChip = { label: "Draft", cls: "bg-amber-100 text-amber-700" };
  } else if (s.published) {
    cta = { label: "Edit", onClick: goEditor, testid: "mk-card-edit", Icon: Pencil };
    secondary = [
      { label: "Preview", onClick: preview, Icon: Eye, testid: "mk-card-preview" },
      { label: "Share", onClick: share, Icon: Share2, testid: "mk-card-share" },
    ];
    statusChip = { label: "Published", cls: "bg-emerald-100 text-emerald-700" };
  }

  return (
    <div data-testid={`media-kit-pro-card-${variant}`}
      className={`overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white ${isProfile ? "p-4" : "mt-6 p-5"}`}>
      <div className="flex items-start gap-3">
        <div className={`flex shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white ${isProfile ? "h-10 w-10" : "h-12 w-12"}`}>
          <FileText className={isProfile ? "h-5 w-5" : "h-6 w-6"} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`font-display font-bold text-zinc-900 ${isProfile ? "text-base" : "text-lg"}`}>{isProfile ? "My Media Kit" : "Your Media Kit"}</h3>
            {Badge}
            {statusChip && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusChip.cls}`}>{statusChip.label}</span>}
          </div>
          <p className={`mt-1 text-zinc-600 ${isProfile ? "text-xs" : "text-sm"}`}>
            Showcase your content, audience and previous collaborations in a polished, shareable kit.
          </p>
          {!s.is_pro && (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-emerald-700"><Crown className="h-3.5 w-3.5" /> Included with Creator Pro</p>
          )}
          {s.is_pro && s.share_paused_by_expiry && (
            <p className="mt-1 text-xs text-amber-600">Sharing was paused — re-enable it in the editor.</p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {cta && (
              <Button data-testid={cta.testid} size={isProfile ? "sm" : "default"} onClick={cta.onClick}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                {cta.Icon ? <cta.Icon className="mr-1.5 h-4 w-4" /> : <Sparkles className="mr-1.5 h-4 w-4" />}{cta.label}
              </Button>
            )}
            {secondary.map((a) => (
              <Button key={a.testid} data-testid={a.testid} size={isProfile ? "sm" : "default"} variant="outline" onClick={a.onClick} className="rounded-full">
                <a.Icon className="mr-1.5 h-4 w-4" />{a.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
